import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";

const CODE_ALPHABET = "ABCDEFGHJKLMNPQRSTUVWXYZ23456789";

function generateCode(name: string): string {
  const clean = name.replace(/[^a-zA-Z]/g, "").toUpperCase().slice(0, 6);
  // Cryptographically strong suffix so codes cannot be guessed or enumerated.
  const bytes = new Uint8Array(10);
  crypto.getRandomValues(bytes);
  const rand = Array.from(bytes, (b) => CODE_ALPHABET[b % CODE_ALPHABET.length]).join("");
  return `${clean || "GATOR"}${rand}`;
}

export function useReferralCode() {
  const { user } = useAuth();

  return useQuery({
    queryKey: ["referral-code", user?.id],
    enabled: !!user,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("referral_codes")
        .select("*")
        .eq("user_id", user!.id)
        .maybeSingle();

      if (data) return data;

      // Auto-generate if not exists
      const displayName = user!.user_metadata?.full_name || user!.user_metadata?.name || user!.email?.split("@")[0] || "USER";
      const code = generateCode(displayName);

      const { data: newCode, error: insertError } = await supabase
        .from("referral_codes")
        .insert({ user_id: user!.id, code })
        .select()
        .single();

      if (insertError) throw insertError;
      return newCode;
    },
  });
}

export function useReferralStats() {
  const { user } = useAuth();

  return useQuery({
    queryKey: ["referral-stats", user?.id],
    enabled: !!user,
    queryFn: async () => {
      const { data: referrals = [] } = await supabase
        .from("referrals")
        .select("*")
        .eq("referrer_id", user!.id);

      const total = referrals?.length || 0;
      const completed = referrals?.filter((r: any) => r.status === "completed").length || 0;
      const pending = referrals?.filter((r: any) => r.status === "pending").length || 0;
      const weeksEarned = referrals?.filter((r: any) => r.referrer_reward_applied).length || 0;

      return { total, completed, pending, weeksEarned, referrals: referrals || [] };
    },
  });
}
